import { useState } from 'react';
import styled from 'styled-components';

import { useNavigate } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { useLocalStorage } from '@xing-com/crate-hooks-use-web-storage';
import { AiSearchBanner } from '@xing-com/crate-jobs-domain-search-ai-search-banner';
import { SearchBox } from '@xing-com/crate-jobs-domain-search-components-search-box';
import { useSearchLocation } from '@xing-com/crate-jobs-hooks';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { NoSSR } from '@xing-com/crate-utils-no-ssr';
import { useExperiment } from '@xing-com/hub';
import { mediaConfined, mediaTopBarHidden } from '@xing-com/layout-tokens';
import { spaceM, spaceXL, xdlColorTextOnLight } from '@xing-com/tokens';

import {
  Background,
  Box,
  FakeInput,
  SearchboxStartpageMetadata,
  Filters,
  Teaser,
  Title,
  Keywords,
} from './components';
import { PrideGradient } from './components/pride-gradient';

const LOCAL_STORAGE_KEY = 'guidance-tip-box';
type TipBoxState = 'hidden' | 'input' | 'promotions';

type Props = {
  withTeaser?: boolean;
  withFilters?: boolean;
  withPromotions?: boolean;
  spacious?: boolean;
  autofillBusinessCity?: boolean;
  isStartPage?: boolean;
  withKeywords?: boolean;
  withAiSearch?: boolean;
  className?: string;
};

const FULL_REMOTE_FILTER_VALUE = 'FULL_REMOTE.050e26';
const PART_TIME_FILTER_VALUE = 'PART_TIME.58889d';
const FLEXITIME_FILTER_VALUE = '1.795d28';

export const FindJobsSearchBar: React.FC<Props> = ({
  withTeaser = false,
  withFilters = false,
  withPromotions = false,
  spacious = false,
  autofillBusinessCity = false,
  isStartPage,
  withKeywords = false,
  withAiSearch = false,
  className,
}) => {
  const navigate = useNavigate();
  const [keywords, setKeywords] = useState('');
  const { location, cityId, onChangeLocation, onSelectLocation } =
    useSearchLocation({ skipBusinessCityQuery: !autofillBusinessCity });

  const { isLoggedOut } = useLoginState();
  const isAiSearchForced = useExperiment('ABACUS-468') === 'B' || withAiSearch;

  const trackingParameter = isStartPage
    ? '?sc_o=losp_jobs_search_button_click&sc_o_PropActionOrigin=losp_job_search'
    : '?sc_o=jobs_search_button';

  // Extra filters
  const [isFullRemote, setIsFullRemote] = useState(false);
  const [isPartTime, setIsPartTime] = useState(false);
  const [isFlexitime, setIsFlexitime] = useState(false);

  // Tooltip state & functions
  const [tipBoxState, setTipBoxState] = useLocalStorage<TipBoxState>(
    LOCAL_STORAGE_KEY,
    'input'
  );
  const onClose = (): void => {
    setTipBoxState('hidden');
  };
  const onClickNext = (state: Exclude<TipBoxState, 'hidden'>) => (): void => {
    if (state === 'promotions') {
      setTipBoxState('hidden');
    } else {
      setTipBoxState('promotions');
    }
  };

  const onSubmit = (): void => {
    const params = new URLSearchParams(trackingParameter);

    if (keywords) {
      params.set('keywords', keywords);
    }
    if (location) {
      params.set('location', location);
    }
    if (cityId) {
      params.set('cityId', cityId);
    }

    // Extra filters
    if (isFullRemote) {
      params.set('remoteOption', FULL_REMOTE_FILTER_VALUE);
    }
    if (isPartTime) {
      params.set('employmentType', PART_TIME_FILTER_VALUE);
    }
    if (isFlexitime) {
      params.set('benefit', FLEXITIME_FILTER_VALUE);
    }

    navigate(`${ROUTES.search}?${params.toString()}`);
  };

  const filterProps = {
    isFullRemote,
    setIsFullRemote,
    isPartTime,
    setIsPartTime,
    isFlexitime,
    setIsFlexitime,
  };

  const showBanner = !isAiSearchForced && !isLoggedOut;

  return (
    <Background $spacious={spacious} className={className}>
      <NoSSR>
        <PrideGradient />
      </NoSSR>
      {isStartPage ? <SearchboxStartpageMetadata /> : null}
      <Box>
        {showBanner ? <StyledBanner propActionOrigin="jobs_find_jobs" /> : null}
        {withTeaser ? <Teaser withBanner={showBanner} /> : null}

        <Title spacious={spacious} isAiSearchForced={isAiSearchForced} />
        {isAiSearchForced ? (
          <FakeInput
            tipBoxState={tipBoxState}
            onClose={onClose}
            onClickNext={onClickNext}
          />
        ) : (
          <SearchBox
            keywordsInput={keywords}
            locationInput={location}
            onChangeKeywords={setKeywords}
            onSelectKeywords={setKeywords}
            onChangeLocation={onChangeLocation}
            onSelectLocation={onSelectLocation}
            onSubmit={onSubmit}
            withFilters={withFilters}
            withPromotions={withPromotions}
            landingFilters={<Filters {...filterProps} />}
          />
        )}
        <LoveIsLove href="https://www.xing.com/news/pages/diversitat-inklusion-2658">
          #loveislove
        </LoveIsLove>
      </Box>
      {withKeywords ? (
        <Keywords
          isAiSearchForced={isAiSearchForced}
          tipBoxState={tipBoxState}
          onClose={onClose}
          onClickNext={onClickNext}
          isStartPage={isStartPage}
        />
      ) : null}
    </Background>
  );
};

const StyledBanner = styled(AiSearchBanner)`
  margin: 0 auto ${spaceM};

  @media ${mediaConfined} {
    display: block;
    width: fit-content;
  }

  @media ${mediaTopBarHidden} {
    display: none;
  }
`;

const LoveIsLove = styled.a`
  display: block;
  color: ${xdlColorTextOnLight};
  text-align: center;
  font-weight: 700;
  padding-top: ${spaceXL};
  font-size: 16px;
  line-height: 22px;
`;
